import { EntityDefinition, AppUserPermissions } from 'types';
import AxiosHelper from './AxiosHelper';
import Axios, { CancelTokenSource } from 'axios';
//#region Smo
import IMultiTenantDto from 'types/__models__/smo/IMultiTenantDto';
import { getCurrentTenantId } from 'features/tenants/helpers/TenantHelper';
//#endregion Smo

/**
 * Common type used to index entity definitions by different strings
 *
 * @export
 * @interface EntityDefinitionDictionary
 */
export interface EntityDefinitionDictionary {
	[index: string]: EntityDefinition;
}

/**
 * Default map of entity definitions
 */
const entityDefinitions: EntityDefinitionDictionary = {};

/**
 * Registers an entity definition. This is usually called from the EntityDefinition constructor.
 * @param entityDefinition
 */
export const registerEntityDefinition = (entityDefinition: EntityDefinition) => {
	if (entityDefinition.code) {
		entityDefinitions[entityDefinition.code] = entityDefinition;
	} else {
		const warningMessage =
			"Trying to register an entity definition with an empty 'code'.\n" +
			`Singular Name: ${entityDefinition.singularName}\n` +
			`Plural Name: ${entityDefinition.pluralName}\n` +
			`Base Ajax Url: ${entityDefinition.baseAjaxUrl}`;

		console.warn(warningMessage, entityDefinition);
	}
};

/**
 * Returns all entity definitions in a dictionary. We could return the object directly, but this will
 * likely be needed when dealing with dynamic entity definitions (attributes).
 */
export const getEntityDefinitions = () => {
	return entityDefinitions;
};

/**
 * Returns entity definitions mapped by their `baseAjaxRoute`. This isn't ideal but it's the only way to
 * consistently get a match between the client and the server.
 */
export const getEntityDefinitionsByBaseAjaxUrl = (): EntityDefinitionDictionary => {
	return Object.keys(entityDefinitions).reduce((acc, value) => {
		const currentEntityDefinition = entityDefinitions[value];
		acc[currentEntityDefinition.baseAjaxRoute] = currentEntityDefinition;

		// Map current entity definition by a predefined key. This is necessary
		// for exporting entity types whose baseAjaxRoute is dynamic
		const customExportKey = currentEntityDefinition?.exportOptions?.typeKey;
		if (customExportKey != null && acc[customExportKey] == null) {
			acc[customExportKey] = currentEntityDefinition;
		}

		return acc;
	}, {});
};

/**
 * Returns entity definitions mapped by their `baseRoute`.
 */
export const getEntityDefinitionsByBaseRoute = (): EntityDefinitionDictionary => {
	return Object.keys(entityDefinitions).reduce((acc, value) => {
		const currentEntityDefinition = entityDefinitions[value];
		acc[currentEntityDefinition.baseRoute] = currentEntityDefinition;
		return acc;
	}, {});
};

// this will act as a way to cache the request to load importable entity definitions from the server.
let importableEntityDefinitionPromise: Promise<any>;
let exportableEntityDefinitionPromise: Promise<any>;

interface ImportableTypesResponse {
	importTypes: ImportableType[];
}

interface ImportableType {
	name: string;
	typeName: string;
	entityTypeId: string;
	indexRoute: string;
	importRoute: string;
	isAuthorized: boolean;
}

interface ExportableTypesResponse {
	exportTypes: ExportableType[];
}

interface ExportableType {
	name: string;
	typeName: string;
	indexRoute: string;
	exportRoute: string;
	isAuthorized: boolean;
}

/**
 * Returns all importable entity definitions from the server.
 */
export const getImportableEntityDefinitions = async (): Promise<ImportableTypesResponse> => {
	if (importableEntityDefinitionPromise) {
		return importableEntityDefinitionPromise;
	}

	importableEntityDefinitionPromise = new Promise(async (resolve, reject) => {
		try {
			const importableTypes = await AxiosHelper.get<ImportableTypesResponse>('/ajax/import/types').then(
				(response) => response.data,
			);

			resolve(importableTypes);
		} catch (error) {
			reject(error);
		}
	});

	return importableEntityDefinitionPromise;
};

/**
 * Returns all exportable entity definitions from the server.
 */
export const getExportableEntityDefinitions = async (): Promise<ExportableTypesResponse> => {
	if (exportableEntityDefinitionPromise) {
		return exportableEntityDefinitionPromise;
	}

	exportableEntityDefinitionPromise = new Promise(async (resolve, reject) => {
		try {
			const exportableTypes = await AxiosHelper.get<ExportableTypesResponse>('/ajax/export/types').then(
				(response) => response.data,
			);

			resolve(exportableTypes);
		} catch (error) {
			reject(error);
		}
	});

	return exportableEntityDefinitionPromise;
};

const mappedImportableEntityDefinitionsCache = {};
const mappedExportableEntityDefinitionsCache = {};

/**
 * Returns a map of importable entity definitions based on a provided mapping.
 * @param indexByFn
 * @param cacheKey
 */
export const getMappedImportableEntityDefinitions = async (
	indexByFn: (importableType: ImportableType, entityDefinition: EntityDefinition) => string = (_, entityDefinition) =>
		entityDefinition.code,
	cacheKey: string = '',
) => {
	if (cacheKey && mappedImportableEntityDefinitionsCache[cacheKey]) {
		return mappedImportableEntityDefinitionsCache[cacheKey];
	}

	const importableTypes = await getImportableEntityDefinitions();

	const entityDefinitionsByBaseAjaxUrl = getEntityDefinitionsByBaseAjaxUrl();
	const importableEntityDefinitions: EntityDefinitionDictionary = {};

	for (const importableType of importableTypes.importTypes) {
		// check if we have a match between the client-side entity definitions and the server-side
		const match = entityDefinitionsByBaseAjaxUrl[importableType.indexRoute];
		if (match) {
			importableEntityDefinitions[indexByFn(importableType, match)] = match;
		} else {
			console.warn(
				`No match found for importable type ${importableType.name} with index route ${importableType.indexRoute}`,
			);
		}
	}

	if (cacheKey) {
		mappedImportableEntityDefinitionsCache[cacheKey] = importableEntityDefinitions;
	}

	return importableEntityDefinitions;
};

/**
 * Returns a map of exportable entity definitions based on a provided mapping.
 * @param indexByFn
 * @param cacheKey
 */
export const getMappedExportableEntityDefinitions = async (
	indexByFn: (importableType: ExportableType, entityDefinition: EntityDefinition) => string = (_, entityDefinition) =>
		entityDefinition.code,
	cacheKey: string = '',
) => {
	if (cacheKey && mappedExportableEntityDefinitionsCache[cacheKey]) {
		return mappedExportableEntityDefinitionsCache[cacheKey];
	}

	const exportableTypes = await getExportableEntityDefinitions();

	const entityDefinitionsByBaseAjaxUrl = getEntityDefinitionsByBaseAjaxUrl();
	const exportableEntityDefinitions: EntityDefinitionDictionary = {};

	for (const exportableType of exportableTypes.exportTypes) {
		// check if we have a match between the client-side entity definitions and the server-side
		const match = entityDefinitionsByBaseAjaxUrl[exportableType.indexRoute];
		if (match) {
			exportableEntityDefinitions[indexByFn(exportableType, match)] = match;
		}
	}

	if (cacheKey) {
		mappedExportableEntityDefinitionsCache[cacheKey] = exportableEntityDefinitions;
	}

	return exportableEntityDefinitions;
};

// this will act as a way to cache the requests to load tooltips by entity definition.
let tooltipPromises: { [code: string]: Promise<any> | undefined };

/**
 * Get cached tooltip promise by key
 * @param key
 */
export const getTooltipPromiseByKey = (code: string): Promise<any> | undefined => {
	return tooltipPromises && tooltipPromises[code];
};

/**
 * Set cached tooltip promise by key
 */
export const setTooltipPromiseByKey = (code: string, value: Promise<any> | undefined): void => {
	if (!tooltipPromises) {
		tooltipPromises = {};
	}

	tooltipPromises[code] = value;
};

/**
 * Clear tooltip cache
 */
export const clearTooltipCache = (): void => {
	tooltipPromises = {};
};

/**
 * Loads cached tooltip data based on the entity definition
 */
export const getTooltipsForEntity = (
	entityDefinition: EntityDefinition,
	tooltipRequestToken: CancelTokenSource,
	errorCallback: () => void,
): Promise<any> => {
	const cachedPromise = getTooltipPromiseByKey(entityDefinition.code);

	if (cachedPromise) {
		return cachedPromise;
	}

	const url = `${entityDefinition.baseAjaxRoute}/tooltips`;

	// Handle other requested tooltip keys
	let query = '';
	if (entityDefinition.extraTooltipKeys) {
		query += (query === '' ? '?' : '&') + 'extraTooltipKeys=' + entityDefinition.extraTooltipKeys.join(',');
	}

	//#region Commerce
	const anyDefinition = entityDefinition as any;
	if (anyDefinition.attributeCode && anyDefinition.attributeCode !== '') {
		query += (query === '' ? '?' : '&') + 'attributeCode=' + anyDefinition.attributeCode;
	}
	//#endregion

	const singularName = entityDefinition.singularName;

	const loadTooltipDataPromise = AxiosHelper.get(url + query, { cancelToken: tooltipRequestToken.token }).catch(
		(error) => {
			if (Axios.isCancel(error)) {
				return;
			}

			console.error(`An error occurred while loading tooltips for ${singularName}.`);

			errorCallback();
		},
	);

	setTooltipPromiseByKey(entityDefinition.code, loadTooltipDataPromise);

	return loadTooltipDataPromise;
};

/**
 * Does the logged in user have delete permission to the given baseRoute
 * @param baseRoute
 * @param authorizedBaseRoutesData
 */
export const hasDeletePermission = (
	entityDefinition: EntityDefinition,
	authorizedBaseRoutesData: AppUserPermissions,
) => {
	if (authorizedBaseRoutesData.isPowerUser) {
		return true;
	}

	const { baseAjaxRoute, parentBaseAjaxRoute } = entityDefinition;
	let permissions = authorizedBaseRoutesData.authorizedBaseRoutes[baseAjaxRoute];

	if (permissions) {
		return permissions[2];
	} else {
		if (!parentBaseAjaxRoute || parentBaseAjaxRoute == baseAjaxRoute) {
			return false;
		}
		permissions = authorizedBaseRoutesData.authorizedBaseRoutes[parentBaseAjaxRoute];
		if (!permissions) {
			// no permissions
			return false;
		}
		return permissions[2];
	}
};

/**
 * Does the logged in user have list permission to the given baseRoute
 * @param baseRoute
 * @param authorizedBaseRoutesData
 */
export const hasListPermission = (entityDefinition: EntityDefinition, authorizedBaseRoutesData: AppUserPermissions) => {
	if (authorizedBaseRoutesData.isPowerUser) {
		return true;
	}

	const { baseAjaxRoute, parentBaseAjaxRoute } = entityDefinition;
	let permissions = authorizedBaseRoutesData.authorizedBaseRoutes[baseAjaxRoute];

	if (permissions) {
		return permissions[0];
	} else {
		if (!parentBaseAjaxRoute || parentBaseAjaxRoute == baseAjaxRoute) {
			return false;
		}
		permissions = authorizedBaseRoutesData.authorizedBaseRoutes[parentBaseAjaxRoute];
		if (!permissions) {
			// no permissions
			return false;
		}
		return permissions[0];
	}
};

/**
 * Does the logged in user have create/edit permission to the given baseRoute
 * @param baseRoute
 * @param authorizedBaseRoutesData
 */
export const hasCreateEditPermission = (
	entityDefinition: EntityDefinition,
	authorizedBaseRoutesData: AppUserPermissions,
) => {
	if (authorizedBaseRoutesData.isPowerUser) {
		return true;
	}

	const { baseAjaxRoute, parentBaseAjaxRoute } = entityDefinition;
	let permissions = authorizedBaseRoutesData.authorizedBaseRoutes[baseAjaxRoute];

	if (permissions) {
		return permissions[1];
	} else {
		if (!parentBaseAjaxRoute || parentBaseAjaxRoute == baseAjaxRoute) {
			return false;
		}
		permissions = authorizedBaseRoutesData.authorizedBaseRoutes[parentBaseAjaxRoute];
		if (!permissions) {
			// no permissions
			return false;
		}
		return permissions[1];
	}
};

/**
 * Does the logged in user have any list permission
 * @param authorizedBaseRoutesData
 */
export const hasAnyListPermission = (authorizedBaseRoutesData: {
	isPowerUser: boolean;
	hasAnyListPermission: boolean;
}) => {
	if (authorizedBaseRoutesData.isPowerUser) {
		return true;
	}

	return authorizedBaseRoutesData.hasAnyListPermission;
};

/**
 * Does the logged in user have any create/edit permission
 * @param authorizedBaseRoutesData
 */
export const hasAnyCreateEditPermission = (authorizedBaseRoutesData: {
	isPowerUser: boolean;
	hasAnyEditPermission: boolean;
}) => {
	if (authorizedBaseRoutesData.isPowerUser) {
		return true;
	}

	return authorizedBaseRoutesData.hasAnyEditPermission;
};

/**
 * Retrieves the edit route by entity singular name.
 * @param singularName The singular name of the entity.
 * @returns The edit route for the entity with the specified singular name, or an empty string if not found.
 */
export const getEditRouteByEntitySingularName = (singularName: string): string => {
	const entityDefinitions = getEntityDefinitionsByBaseRoute();
	const keysWithSingularName = Object.keys(entityDefinitions).find(
		(key) => entityDefinitions[key].singularName.replace(/ /g, '') === singularName.replace(/ /g, ''),
	);
	return keysWithSingularName ? entityDefinitions[keysWithSingularName].baseEditRoute : '';
};

//#region Smo
/**
 * Is the given record owned by currently selected tenant?
 */
export const isRecordOwnedByCurrentTenant = (record: object): boolean => {
	const multiTenantDto = record as IMultiTenantDto;
	if (multiTenantDto.tenantId === undefined) {
		return true;
	}

	// record without tenant id is allowed to be edited by any admin user
	if (multiTenantDto.tenantId == null) {
		return true;
	}

	const currentTenantId = getCurrentTenantId();
	if (currentTenantId == null) {
		return false;
	}
	return currentTenantId == multiTenantDto.tenantId;
};

//#endregion Smo
